import Chart from 'chart.js/auto';

/**
 * Calculator Components
 * 
 * Handles range slider inputs and display updates for the fund calculators.
 *
 * Attributes:
 * - data-reinvestment-amount / data-reinvestment-contributions / data-reinvestment-term: Range inputs
 * - data-reinvestment-amount-display / etc: Display spans for formatted values
 * - data-distribution-amount / data-distribution-term: Range inputs
 * - data-distribution-amount-display / etc: Display spans
 *
 * Example Usage:
 * new Reinvestment();
 * new Distribution();
 */

function formatCurrency(value) {
	return '$' + Number(value).toLocaleString();
}

/**
 * Maps slider position (0-100) to exponential value scale
 * 0 = $1,000, 50 = $250,000, 100 = $5,000,000
 */
function getExponentialSliderValue(position, min = 1000, max = 5000000, midpoint = 250000) {
	// Calculate exponent for curve that hits midpoint at position 50
	// Formula: value = min + (max - min) * (position/100)^exponent
	// At position 50: midpoint = min + (max - min) * (0.5)^exponent
	const ratio = (midpoint - min) / (max - min);
	const exponent = Math.log(ratio) / Math.log(0.5);

	const normalizedPos = position / 100;
	const rawValue = min + (max - min) * Math.pow(normalizedPos, exponent);

	// Round to satisfying "nice" numbers based on magnitude
	let roundTo;
	if (rawValue < 10000) {
		roundTo = 1000; // 1K, 2K, 5K, 10K
	} else if (rawValue < 100000) {
		roundTo = 5000; // 10K, 15K, 20K, 50K, 100K
	} else if (rawValue < 1000000) {
		roundTo = 10000; // 100K, 110K, 250K, 500K, 990K
	} else {
		roundTo = 50000; // 1M, 1.05M, 2M, 5M
	}

	return Math.round(rawValue / roundTo) * roundTo;
}

/**
 * Converts value back to slider position for initialization
 */
function getSliderPositionFromValue(value, min = 1000, max = 5000000, midpoint = 250000) {
	const ratio = (midpoint - min) / (max - min);
	const exponent = Math.log(ratio) / Math.log(0.5);

	const normalizedValue = (value - min) / (max - min);
	const position = Math.pow(normalizedValue, 1 / exponent) * 100;
	return Math.round(position);
}

function updateRangeProgress(input) {
	const min = parseFloat(input.min) || 0;
	const max = parseFloat(input.max) || 100;
	const val = parseFloat(input.value) || 0;
	const progress = ((val - min) / (max - min)) * 100;
	input.style.setProperty('--range-progress', progress + '%');
}

// Init all range inputs on page
document.addEventListener('DOMContentLoaded', () => {
	document.querySelectorAll('input[type="range"]').forEach(input => {
		updateRangeProgress(input);
		input.addEventListener('input', () => updateRangeProgress(input));
	});
});

export class Reinvestment {
	constructor(inputContainer, outputContainer) {
		this.inputContainer = inputContainer;
		this.outputContainer = outputContainer;
		if (!this.inputContainer) return;

		this.amount = this.inputContainer.querySelector('[data-reinvestment-amount]');
		this.contributions = this.inputContainer.querySelector('[data-reinvestment-contributions]');
		this.term = this.inputContainer.querySelector('[data-reinvestment-term]');

		this.amountDisplay = this.inputContainer.querySelector('[data-reinvestment-amount-display]');
		this.contributionsDisplay = this.inputContainer.querySelector('[data-reinvestment-contributions-display]');
		this.termDisplay = this.inputContainer.querySelector('[data-reinvestment-term-display]');

		this.resultDisplay = this.outputContainer?.querySelector('[data-reinvestment-result]');
		this.summaryInitial = this.outputContainer?.querySelector('[data-reinvestment-summary-initial]');
		this.summaryContributions = this.outputContainer?.querySelector('[data-reinvestment-summary-contributions]');
		this.summaryWorth = this.outputContainer?.querySelector('[data-reinvestment-summary-worth]');
		this.summaryYears = this.outputContainer?.querySelectorAll('[data-reinvestment-summary-years]') || [];

		this.chartCanvas = this.outputContainer?.querySelector('[data-reinvestment-chart]');
		this.chart = null;

		this.rate = parseFloat(this.inputContainer.getAttribute('data-reinvestment-rate'));
		this.compoundPeriod = 12; // compounded monthly
		this.contributionPeriod = 12; // monthly contributions

		this.initChart();
		this.init();
	}

	init() {
		if (this.amount) {
			// Handle exponential slider
			const min = parseFloat(this.amount.getAttribute('data-slider-min')) || 1000;
			const max = parseFloat(this.amount.getAttribute('data-slider-max')) || 5000000;
			const midpoint = parseFloat(this.amount.getAttribute('data-slider-midpoint')) || 250000;

			const updateAmount = () => {
				const position = parseFloat(this.amount.value);
				const value = getExponentialSliderValue(position, min, max, midpoint);
				this.amount.setAttribute('data-slider-value', value);
				if (this.amountDisplay) this.amountDisplay.textContent = formatCurrency(value);
				this.calculate();
			};

			this.amount.addEventListener('input', updateAmount);
			updateAmount();
		}

		if (this.contributions) {
			this.contributions.addEventListener('input', () => {
				if (this.contributionsDisplay) this.contributionsDisplay.textContent = formatCurrency(this.contributions.value);
				this.calculate();
			});
		}

		if (this.term) {
			this.term.addEventListener('input', () => {
				if (this.termDisplay) this.termDisplay.textContent = this.term.value + ' years';
				this.calculate();
			});
		}

		this.calculate();
	}

	initChart() {
		if (!this.chartCanvas) return;

		const data = this.getChartData();
		this.chart = new Chart(this.chartCanvas.getContext('2d'), {
			type: 'bar',
			data: data,
			options: {
				responsive: true,
				maintainAspectRatio: false,
				plugins: {
					legend: {
						display: true,
						position: 'bottom'
					},
					tooltip: {
						mode: 'index',
						intersect: false,
						callbacks: {
							label: function(context) {
								return context.dataset.label + ': $' + parseInt(context.raw).toLocaleString('en-US');
							}
						}
					}
				},
				scales: {
					x: {
						stacked: true,
						title: { display: true, text: 'Year' }
					},
					y: {
						stacked: true,
						title: { display: true, text: 'Balance' },
						ticks: {
							callback: function(value) {
								return '$' + parseInt(value).toLocaleString('en-US');
							}
						}
					}
				}
			}
		});
	}

	getChartData() {
		const P = this.amount ? Number(this.amount.getAttribute('data-slider-value') || this.amount.value) : 0;
		const r = this.rate / 100;
		const c = Number(this.contributions?.value || 0);
		const n = this.compoundPeriod;
		const n2 = this.contributionPeriod;
		const t = Number(this.term?.value || 0);
		const currentYear = new Date().getFullYear();

		const labels = [];
		const principalData = [];
		const simpleInterestData = [];
		const compoundingData = [];

		let yearlySimpleInterest = 0;
		let yearlyContribution = 0;

		for (let i = 1; i <= t; i++) {
			labels.push(currentYear + i);
			const principal = P + (c * n2 * i);

			if (r > 0) {
				const x = Math.pow(1 + r / n, n * i);
				const compoundInterest = P * x;
				const contributionInterest = c * (x - 1) / (r / n2);
				const interest = Math.round(compoundInterest + contributionInterest - principal);

				// Simple interest calculation per month
				for (let month = 1; month <= 12; month++) {
					const monthlyContribution = (i === 1) ? yearlyContribution + c * (month - 1) : yearlyContribution + c * month;
					const monthlySimpleInterest = (P + monthlyContribution) * (r / 12);
					yearlySimpleInterest += monthlySimpleInterest;
				}
				yearlyContribution = (i === 1) ? c * 11 + yearlyContribution : yearlyContribution + c * 12;

				principalData.push(principal);
				simpleInterestData.push(Math.round(yearlySimpleInterest));
				compoundingData.push(Math.round(interest - yearlySimpleInterest));
			} else {
				principalData.push(principal);
				simpleInterestData.push(0);
				compoundingData.push(0);
			}
		}

		return {
			labels: labels,
			datasets: [
				{
					label: 'Invested capital',
					backgroundColor: '#374253',
					data: principalData
				},
				{
					label: 'Simple return',
					backgroundColor: '#9BB996',
					data: simpleInterestData
				},
				{
					label: 'Compounding return',
					backgroundColor: '#F89D6F',
					data: compoundingData
				}
			]
		};
	}

	updateChart() {
		if (!this.chart) return;
		const data = this.getChartData();
		this.chart.data.labels = data.labels;
		this.chart.data.datasets[0].data = data.datasets[0].data;
		this.chart.data.datasets[1].data = data.datasets[1].data;
		this.chart.data.datasets[2].data = data.datasets[2].data;
		this.chart.update();
	}

	calculate() {
		const P = this.amount ? Number(this.amount.getAttribute('data-slider-value') || this.amount.value) : 0;
		const r = this.rate / 100;
		const c = Number(this.contributions?.value || 0);
		const n = this.compoundPeriod;
		const n2 = this.contributionPeriod;
		const t = Number(this.term?.value || 0);

		// Compound interest: P * (1 + r/n)^(n*t)
		const x = Math.pow(1 + r / n, n * t);
		const compoundInterest = P * x;

		// Future value of contributions: c * ((1 + r/n)^(n*t) - 1) / (r/n2)
		const contributionInterest = r > 0 ? c * (x - 1) / (r / n2) : c * n2 * t;

		const balance = Math.round(compoundInterest + contributionInterest);

		if (this.resultDisplay) {
			this.resultDisplay.textContent = '$' + balance.toLocaleString('en-US');
		}
		if (this.summaryInitial) {
			this.summaryInitial.textContent = formatCurrency(P);
		}
		if (this.summaryContributions) {
			this.summaryContributions.textContent = formatCurrency(c);
		}
		if (this.summaryWorth) {
			this.summaryWorth.textContent = '$' + balance.toLocaleString('en-US');
		}
		if (this.summaryYears) {
			this.summaryYears.forEach(year => {
				year.textContent = t;
			});
		}

		this.updateChart();
	}
}

export class Distribution {
	constructor(inputContainer, outputContainer) {
		this.inputContainer = inputContainer;
		this.outputContainer = outputContainer;
		if (!this.inputContainer) return;

		this.amount = this.inputContainer.querySelector('[data-distribution-amount]');
		this.amountDisplay = this.inputContainer.querySelector('[data-distribution-amount-display]');
		this.resultDisplay = this.outputContainer?.querySelector('[data-distribution-result]');
		this.rate = parseFloat(this.inputContainer.getAttribute('data-distribution-rate'));
		this.payment = 12;

		this.init();
	}

	init() {
		if (this.amount) {
			// Handle exponential slider
			const min = parseFloat(this.amount.getAttribute('data-slider-min')) || 1000;
			const max = parseFloat(this.amount.getAttribute('data-slider-max')) || 5000000;
			const midpoint = parseFloat(this.amount.getAttribute('data-slider-midpoint')) || 250000;

			const updateAmount = () => {
				const position = parseFloat(this.amount.value);
				const value = getExponentialSliderValue(position, min, max, midpoint);
				this.amount.setAttribute('data-slider-value', value);
				if (this.amountDisplay) this.amountDisplay.textContent = formatCurrency(value);
				this.calculate();
			};

			this.amount.addEventListener('input', updateAmount);
			updateAmount();
		}

		this.calculate();
	}

	calculate() {
		const amount = this.amount ? Number(this.amount.getAttribute('data-slider-value') || this.amount.value) : 0;
		const gross = (amount * (this.rate / 100)) / this.payment;
		if (this.resultDisplay) {
			this.resultDisplay.textContent = '$' + gross.toFixed(2);
		}
	}
}
